import { useEffect, useRef } from "react";

interface HeroSectionProps {
  tagline: string;
  title: string;
  subtitle: string;
  ctaText: string;
  ctaHref: string;
  secondaryCtaText?: string;
  secondaryCtaHref?: string;
  backgroundImage: string;
}

export default function HeroSection({
  tagline,
  title,
  subtitle,
  ctaText,
  ctaHref,
  secondaryCtaText,
  secondaryCtaHref,
  backgroundImage,
}: HeroSectionProps) {
  const bgRef = useRef<HTMLDivElement>(null);
  const titleLines = title.split("\n").filter(Boolean);

  useEffect(() => {
    const el = bgRef.current;
    if (!el) return;
    const onScroll = () => {
      if (el) el.style.transform = `translateY(${window.scrollY * 0.26}px)`;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <section className="relative min-h-screen flex flex-col overflow-hidden">
      {/* Parallax bg */}
      <div
        ref={bgRef}
        className="absolute inset-0 scale-110 bg-cover bg-top will-change-transform"
        style={{ backgroundImage: `url('${backgroundImage}')` }}
        aria-hidden="true"
      />

      {/* Dark scrim — deliberately leaves top 16% fully transparent so white can own it */}
      <div
        className="absolute inset-0"
        style={{ background: "linear-gradient(to bottom, transparent 0%, transparent 16%, rgba(0,0,0,0.48) 38%, rgba(0,0,0,0.72) 65%, rgba(0,0,0,0.90) 100%)" }}
      />
      {/* WHITE navbar-blend layer — fixed 120px, sits above dark scrim, pure white at top edge */}
      <div
        className="absolute inset-x-0 top-0 pointer-events-none"
        style={{ height: "120px", background: "linear-gradient(to bottom, #ffffff 0%, #ffffff 25%, rgba(255,255,255,0.6) 65%, rgba(255,255,255,0) 100%)", zIndex: 3 }}
      />
      {/* Film grain */}
      <div className="grain-overlay absolute inset-0 pointer-events-none z-[1]" aria-hidden="true" />

      {/* Editorial left sidebar */}
      <div
        className="absolute left-6 xl:left-10 top-1/2 -translate-y-1/2 hidden xl:flex flex-col items-center gap-4 z-10"
        aria-hidden="true"
      >
        <div className="w-px h-20 bg-gradient-to-b from-transparent to-gold/50" />
        <span
          className="text-white/20 font-sans text-[9px] font-bold uppercase tracking-[0.35em]"
          style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
        >
          Automotive Photography · Bremerton, WA
        </span>
        <div className="w-px h-20 bg-gradient-to-b from-gold/50 to-transparent" />
      </div>

      {/* Main content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center text-center px-6 lg:px-12 pt-52 pb-20">

        {/* Tagline */}
        <div className="hero-reveal flex items-center gap-5 mb-12">
          <span className="inline-block w-12 h-px bg-gradient-to-r from-transparent via-gold to-transparent" />
          <p
            className="text-gold font-sans text-[13px] font-bold uppercase tracking-[0.28em]"
            style={{ textShadow: "none" }}
          >
            {tagline}
          </p>
          <span className="inline-block w-12 h-px bg-gradient-to-r from-transparent via-gold to-transparent" />
        </div>

        {/* Headline — massive, magazine-scale Playfair Display */}
        <h1 className="hero-reveal hero-delay-1 font-display font-bold leading-[0.90] mb-12 max-w-6xl w-full">
          {titleLines.map((line, i) => (
            <span
              key={i}
              className={`block ${
                i === titleLines.length - 1
                  ? "text-gold italic"
                  : "text-white"
              }`}
              style={{
                fontSize: "clamp(52px, 9.5vw, 128px)",
                textShadow: "0 2px 8px rgba(0,0,0,0.6), 0 1px 3px rgba(0,0,0,0.5)",
              }}
            >
              {line}
            </span>
          ))}
        </h1>

        {/* Diamond divider */}
        <div className="hero-reveal hero-delay-2 flex items-center gap-6 mb-12 justify-center">
          <div className="w-28 h-px bg-white/10" />
          <div className="w-2.5 h-2.5 bg-gold rotate-45 flex-shrink-0" />
          <div className="w-28 h-px bg-white/10" />
        </div>

        {/* Subtitle */}
        <p
          className="hero-reveal hero-delay-2 text-stone-200 font-sans text-base md:text-[17px] leading-[1.85] max-w-xl mb-16 font-light tracking-wide"
          style={{ textShadow: "0 1px 4px rgba(0,0,0,0.85)" }}
        >
          {subtitle}
        </p>

        {/* CTAs */}
        <div className="hero-reveal hero-delay-3 flex flex-col sm:flex-row items-center gap-5">
          <a
            href={ctaHref}
            className="group inline-flex items-center justify-center gap-3 px-14 py-4 bg-gold text-white font-sans font-bold text-[11px] uppercase tracking-[0.24em] hover:bg-gold-dark transition-all duration-300 whitespace-nowrap cursor-pointer"
            style={{ boxShadow: "0 10px 40px rgba(201,168,76,0.30)" }}
          >
            {ctaText}
            <span className="w-4 h-4 flex items-center justify-center group-hover:translate-x-1 transition-transform duration-300">
              <i className="ri-arrow-right-line"></i>
            </span>
          </a>
          {secondaryCtaText && secondaryCtaHref && (
            <a
              href={secondaryCtaHref}
              target="_blank"
              rel="nofollow noreferrer"
              className="inline-flex items-center justify-center gap-3 px-14 py-4 border border-white/25 text-white/80 font-sans font-semibold text-[11px] uppercase tracking-[0.24em] hover:border-gold/60 hover:text-gold transition-all duration-300 whitespace-nowrap cursor-pointer backdrop-blur-sm"
            >
              {secondaryCtaText}
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-external-link-line text-xs"></i>
              </span>
            </a>
          )}
        </div>
      </div>

      {/* Stats bar — glassy, pinned bottom */}
      <div className="relative z-10 hero-reveal hero-delay-4">
        <div className="bg-black/55 backdrop-blur-xl border-t border-white/8">
          <div className="px-6 lg:px-14 py-5 max-w-7xl mx-auto flex flex-wrap items-center justify-center lg:justify-between gap-6 lg:gap-0">
            {[
              { value: "Bremerton, WA", label: "Based In" },
              { value: "From $450", label: "Transparent Pricing" },
              { value: "2–4 Weeks", label: "Turnaround" },
              { value: "3 Revisions", label: "Included Free" },
              { value: "360-204-4747", label: "Call to Book" },
            ].map((stat, i) => (
              <div key={i} className={`flex flex-col items-center text-center px-6 lg:px-10 ${i > 0 ? "lg:border-l lg:border-white/8" : ""}`}>
                <p className="font-display text-white text-sm md:text-[15px] font-bold tracking-wide">{stat.value}</p>
                <p className="text-stone-500 font-sans text-[10px] uppercase tracking-[0.2em] mt-0.5">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll cue — right edge */}
      <div
        className="absolute right-8 bottom-28 hidden xl:flex flex-col items-center gap-2 z-10"
        aria-hidden="true"
      >
        <div className="w-px h-14 bg-gradient-to-b from-transparent to-gold/60 animate-pulse" />
        <span
          className="text-stone-600 font-sans text-[9px] uppercase tracking-[0.3em]"
          style={{ writingMode: "vertical-rl" }}
        >
          Scroll
        </span>
      </div>
    </section>
  );
}
